import React from 'react';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export default function SupplementationSection() {
  const products = [
    {
      icon: 'ri-capsule-line',
      title: 'Capsules',
      description: 'Advanced oxygen-boosting capsules that enhance oxygen delivery to cells throughout your body.',
      benefits: ['Improved cellular oxygen', 'Enhanced energy levels', 'Better circulation'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/89e3b8cbae433baaede37ee316ae083c.jfif'
    },
    {
      icon: 'ri-drop-line',
      title: 'Sprays & Skin Products',
      description: 'Topical oxygen treatments for skin conditions including eczema, aging, and wound healing.',
      benefits: ['Skin rejuvenation', 'Eczema relief', 'Faster healing'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/891eb307d91497f1e13065bb15119a8e.jfif'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'OxyKits',
      description: 'Comprehensive family healthcare kits distributed through PHCs and ASHA workers for community access.',
      benefits: ['Family coverage', 'Affordable pricing', 'Community distribution'],
      image: 'https://public.readdy.ai/ai/img_res/15f721f528e3b379f6465f412d34828f.jpg'
    }
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Oxygen for Every Body
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Our scientifically-formulated oxygen supplementation products are designed 
            to address various health needs and make oxygen therapy accessible to all.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {products.map((product, index) => (
            <div 
              key={index}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="aspect-video overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6]/10">
                    <i className={`${product.icon} text-2xl text-[#12B6B6]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#111111]">{product.title}</h3>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>

                {/* Benefits */}
                <div className="space-y-2 mb-6">
                  {product.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-2">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-[#12B6B6]"></i>
                      <span className="text-sm text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>

                <Link to="/supplementation">
                  <Button variant="outline" size="sm" className="w-full">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Capsules Section */}
        <div className="bg-white rounded-3xl p-12 shadow-lg mb-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-[#111111] mb-4">
                Oxygen in a Capsule. Energy in Every Cell.
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                Our Oxygen Capsules are the world's first solid oxygen supplement, created using advanced Ox66® technology. 
                They deliver bioavailable oxygen directly to your body, helping restore balance, energy, and resilience.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              {/* Left Side - Why Capsules Matter */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Why Capsules Matter</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-flashlight-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Boost Cellular Oxygen</h5>
                      <p className="text-gray-600">Supports mitochondria, the body's powerhouses, for sustained energy.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-shield-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Combat Fatigue</h5>
                      <p className="text-gray-600">Helps reduce tiredness caused by oxygen deficiency.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-heart-pulse-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Immunity & Recovery</h5>
                      <p className="text-gray-600">Strengthens natural defense systems and speeds up recovery from stress or illness.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-smartphone-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Safe & Portable</h5>
                      <p className="text-gray-600">No tanks, no masks—just a simple capsule, anytime, anywhere.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Who Benefits */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Who Benefits Most</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">People with chronic fatigue, breathlessness, or low energy</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Athletes seeking faster recovery and endurance</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Elderly individuals with age-related oxygen decline</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Urban residents exposed to high pollution and low oxygen levels</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Women facing oxygen-related issues (PCOS, sleep disorders, anemia)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works & Safety */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-settings-3-line text-[#12B6B6]"></i>
                  <span>How It Works</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Ox66® releases oxygen in a controlled, safe way inside the body</li>
                  <li>• Works in pH-neutral environments (stomach & intestines)</li>
                  <li>• Enhances natural oxygen delivery without external devices</li>
                </ul>
              </div>

              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-shield-check-line text-[#12B6B6]"></i>
                  <span>Safety & Science</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Classified as a nutraceutical under US FDA guidelines</li>
                  <li>• GRAS-approved (Generally Recognized as Safe)</li>
                  <li>• No adverse effects reported in safe dosages</li>
                  <li>• Designed to supplement, not replace, medical oxygen therapy</li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#12B6B6]/5 to-[#12B6B6]/10 rounded-2xl p-8 text-center">
              <h4 className="text-2xl font-bold text-[#111111] mb-4">
                Sponsor Capsules for Families in Need
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                Every ₹10,000 donation provides oxygen capsules for 2 families for an entire year.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/donate">
                  <Button variant="primary" size="lg">
                    Donate Now
                  </Button>
                </Link>
                <Link to="/supplementation">
                  <Button variant="outline" size="lg">
                    Learn More About Ox66®
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Sprays & Skin Products Section */}
        <div className="bg-white rounded-3xl p-12 shadow-lg mb-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-[#111111] mb-4">
                Skin Loves Oxygen.
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                Our Oxygen Sprays and Skincare products deliver active oxygen directly to the skin, supporting healing, repair, and rejuvenation. 
                Oxygen restores natural radiance while helping fight damage caused by stress, pollution, and aging.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              {/* Left Side - Why It Matters */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Why It Matters</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-heart-pulse-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Accelerates Healing</h5>
                      <p className="text-gray-600">Supports skin repair in conditions like eczema, acne, psoriasis, and wounds.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-time-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Anti-Aging</h5>
                      <p className="text-gray-600">Enhances collagen production, reduces fine lines, and improves elasticity.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-shield-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Barrier Protection</h5>
                      <p className="text-gray-600">Strengthens the skin's natural defense against pollutants.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-drop-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Hydration Boost</h5>
                      <p className="text-gray-600">Oxygenated skin retains more moisture and stays supple.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Who Benefits */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Who Benefits Most</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">People with chronic skin ailments (eczema, psoriasis, acne)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Women & men looking for anti-aging and rejuvenation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Individuals exposed to high pollution or harsh weather</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Patients recovering from surgery or skin trauma</span>
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works & Safety */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-settings-3-line text-[#12B6B6]"></i>
                  <span>How It Works</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Ox66® releases oxygen at the skin's surface</li>
                  <li>• Boosts cell regeneration and microcirculation</li>
                  <li>• Works in a pH-neutral topical base for maximum stability</li>
                </ul>
              </div>

              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-shield-check-line text-[#12B6B6]"></i>
                  <span>Safety & Science</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Dermatologist tested</li>
                  <li>• Free from citrus or acidic ingredients (to protect Ox66® stability)</li>
                  <li>• Gentle for daily use</li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#12B6B6]/5 to-[#12B6B6]/10 rounded-2xl p-8 text-center">
              <h4 className="text-2xl font-bold text-[#111111] mb-4">
                Sponsor Skin Care Kits for Needy Patients
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                Every ₹10,000 donation provides sprays and skin support products for families in underserved communities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/donate">
                  <Button variant="primary" size="lg">
                    Donate Now
                  </Button>
                </Link>
                <Link to="/supplementation">
                  <Button variant="outline" size="lg">
                    Learn More About Skin Benefits
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed OxyKits Section */}
        <div className="bg-white rounded-3xl p-12 shadow-lg mb-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-[#111111] mb-4">
                Oxygen First-Aid for Every Family.
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                OxyKits are affordable, easy-to-use packages containing oxygen capsules, sprays, and patches. 
                They are designed to provide first-response supplementation for families and primary health centers.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              {/* Left Side - Why OxyKits Matter */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Why OxyKits Matter</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-first-aid-kit-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Emergency Relief</h5>
                      <p className="text-gray-600">Quick oxygen support in breathlessness, fatigue, or recovery.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-box-3-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Complete Package</h5>
                      <p className="text-gray-600">Combines capsules, sprays, and educational material.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-community-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Community-Friendly</h5>
                      <p className="text-gray-600">Distributed through PHCs, ASHAs, and NGOs.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#12B6B6]/10 flex-shrink-0 mt-1">
                      <i className="ri-briefcase-line text-[#12B6B6]"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#111111] mb-1">Portable & Accessible</h5>
                      <p className="text-gray-600">Fits in a household medicine box; no cylinders or equipment needed.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Who Benefits */}
              <div>
                <h4 className="text-2xl font-bold text-[#111111] mb-6">Who Benefits Most</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Low-income families without access to regular healthcare</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Patients recovering from respiratory conditions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Elderly individuals at higher risk of oxygen deficiency</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6]"></i>
                    <span className="text-gray-700">Villages and urban slums where medical oxygen access is scarce</span>
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works & Transparency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-settings-3-line text-[#12B6B6]"></i>
                  <span>How It Works</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Each kit contains oxygen capsules (internal support) + topical sprays (skin/wounds) + educational guides</li>
                  <li>• Designed for preventive use and mild deficiency relief</li>
                  <li>• Replenished through CSR or donor-funded supply chains</li>
                </ul>
              </div>

              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <h4 className="text-xl font-bold text-[#111111] mb-4 flex items-center space-x-2">
                  <i className="ri-eye-line text-[#12B6B6]"></i>
                  <span>Transparency & Distribution</span>
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Distributed via ASHA workers, Anganwadi, PHCs, and community health programs</li>
                  <li>• Donor dashboards will show number of kits delivered</li>
                  <li>• Real-time tracking and impact reporting</li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#12B6B6]/5 to-[#12B6B6]/10 rounded-2xl p-8 text-center">
              <h4 className="text-2xl font-bold text-[#111111] mb-4">
                Sponsor OxyKits for Families
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                Every ₹10,000 supports 2 families with a full year of OxyKits and education.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/donate">
                  <Button variant="primary" size="lg">
                    Donate Now
                  </Button>
                </Link>
                <Link to="/donate#csr">
                  <Button variant="outline" size="lg">
                    Partner with CSR
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-white rounded-3xl p-8 text-center shadow-lg">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#111111] mb-4">
              Making Oxygen Therapy Accessible
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Our products are distributed through Primary Health Centers (PHCs) and ASHA workers 
              to ensure affordability and accessibility in rural and underserved communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/supplementation">
                <Button variant="primary" size="lg">
                  Learn More About Products
                </Button>
              </Link>
              <Button variant="outline" size="lg">
                Find Distribution Centers
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
